import { appConfig } from "@/config/app";
import { Auth } from "@/types/ApiResponses/Auth";
import { getCurrentLocale } from "./locale";
import { UserRole } from "@/types/ApiResponses/User";

export const clientRedirect = (path: string): void => {
  const locale = getCurrentLocale();
  window.location.href = `/${locale}${path}`;
};

export const getHomePath = (role: UserRole): string => {
  switch (role) {
    case UserRole.Admin:
      return "/admin/matches-list";
    case UserRole.SubAdmin:
      return "/admin";
    // The credit seller has one working screen.
    case UserRole.CreditSeller:
      return "/admin/credit-sales";
    // Betting-side roles land on the Zero theme bulletin after sign-in.
    case UserRole.Agent:
    case UserRole.Dealer:
    case UserRole.SubDealer:
    case UserRole.User:
      return "/zero/prematch";
    default:
      return appConfig.authenticatedPath;
  }
};

export const getAuthRedirectPath = (auth: Partial<Auth>) => {
  if (!auth.session) {
    return appConfig.unauthenticatedEntry;
  }

  if (!auth.user) {
    return appConfig.authenticatedPath;
  }

  return getHomePath(auth.user.role);
};

/**
 * Whitelist a `?redirect=` value before using it. Blocks the open-redirect
 * attack class: only relative paths inside the app are allowed, and the
 * sign-in / reset-password screens are excluded so a malicious link can't
 * loop the user back into the auth flow.
 */
export const isSafeRedirect = (
  path: string | null | undefined
): path is string =>
  !!path &&
  path.startsWith("/") &&
  !path.startsWith("//") &&
  !path.startsWith("/\\") &&
  !/^\/(?:en|tr)?\/?(?:sign-in|sign-up|reset-password)\b/i.test(path);

/**
 * Strip a leading `/tr` or `/en` locale prefix so the redirect helper can
 * re-prepend the active locale without producing `/tr/tr/...`.
 */
export const stripLocalePrefix = (pathname: string): string =>
  pathname.replace(/^\/(?:en|tr)(?=\/|$)/, "") || "/";
